import React, {useEffect, useRef, useState} from 'react';
import {loadGoogleLibrary} from '../../services/googleMapsLoader';
import {translateService} from '../../services/TranslateService';

export function hasPlaceCoordinates(place: google.maps.places.PlaceResult): boolean {
    const lat = place.geometry?.location?.lat();
    const lng = place.geometry?.location?.lng();
    return Number.isFinite(lat) && Number.isFinite(lng);
}

interface Props {
    inputRef: React.RefObject<HTMLInputElement | null>;
    name: string;
    value: string;
    onChange: (text: string) => void;
    onSelect: (place: google.maps.places.PlaceResult, displayText: string) => void;
}

// Keep one input node while Places downloads: no lost focus, text or keystrokes.
// Google's widget owns predictions and Place Details, including attribution.
export default function LocationInput({inputRef, name, value, onChange, onSelect}: Props) {
    const latest = useRef({onChange, onSelect, value});
    latest.current = {onChange, onSelect, value};
    const start = useRef<() => void>(() => {});
    const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle');
    const [showLoading, setShowLoading] = useState(false);
    const [needsSelection, setNeedsSelection] = useState(false);

    useEffect(() => {
        const input = inputRef.current!;
        let disposed = false;
        let loading = false;
        let widget: google.maps.places.Autocomplete | undefined;
        let listener: google.maps.MapsEventListener | undefined;
        let menu: HTMLElement | undefined;
        let menuObserver: MutationObserver | undefined;
        let revision = 0;
        let selectionRevision: number | undefined;
        let lastSelectedRevision: number | undefined;
        const visibleMenu = () => menu?.getClientRects().length && getComputedStyle(menu).visibility !== 'hidden' ? menu : undefined;
        const beginSelection = () => {selectionRevision = revision;};
        const edit = () => {
            revision++;
            selectionRevision = undefined;
            setNeedsSelection(false);
            latest.current.onChange(input.value);
        };
        const keyDown = (event: KeyboardEvent) => {
            if (event.key === 'Tab' && visibleMenu()?.querySelector('.pac-item-selected')) beginSelection();
            if (event.key !== 'Enter' || event.isComposing || event.keyCode === 229) return;
            if (event.repeat || selectionRevision !== undefined) {event.preventDefault(); event.stopImmediatePropagation(); return;}
            const menu = visibleMenu();
            if (!widget || !menu?.querySelector('.pac-item')) {
                event.preventDefault();
                event.stopImmediatePropagation();
                if (lastSelectedRevision !== revision) setNeedsSelection(true);
                return;
            }
            beginSelection();
            if (!menu.querySelector('.pac-item-selected')) {
                // Use the widget's normal keyboard selection path; do not
                // geocode the text or fetch predictions a second time.
                input.dispatchEvent(new KeyboardEvent('keydown', {
                    key: 'ArrowDown', code: 'ArrowDown', keyCode: 40, which: 40, bubbles: true,
                }));
            }
        };
        const pointerDown = (event: Event) => {
            if (document.activeElement === input && event.target instanceof Element &&
                event.target.closest('.pac-item') && visibleMenu()?.contains(event.target)) beginSelection();
        };
        input.addEventListener('input', edit);
        input.addEventListener('keydown', keyDown, true);
        document.addEventListener('mousedown', pointerDown, true);
        document.addEventListener('touchstart', pointerDown, true);
        start.current = () => {
            if (widget || loading) return;
            loading = true;
            setStatus('loading');
            const loadingTimer = window.setTimeout(() => setShowLoading(true), 300);
            void loadGoogleLibrary('places').then(() => {
                if (disposed) return;
                const previousMenus = new Set(document.querySelectorAll('.pac-container'));
                // The legacy widget attaches its implementation asynchronously,
                // even after importLibrary resolves. Wait for its own menu before
                // replaying text entered during the download, and scope keyboard
                // selection to this widget rather than another WP address field.
                menuObserver = new MutationObserver(() => {
                    menu = Array.from(document.querySelectorAll<HTMLElement>('.pac-container'))
                        .find(candidate => !previousMenus.has(candidate));
                    if (!menu) return;
                    menuObserver?.disconnect();
                    if (revision > 0 && input.value && document.activeElement === input) {
                        input.dispatchEvent(new FocusEvent('focus'));
                        const key = input.value.slice(-1);
                        const keyCode = key.toUpperCase().charCodeAt(0);
                        input.dispatchEvent(new KeyboardEvent('keydown', {key, keyCode, which: keyCode, bubbles: true}));
                        input.dispatchEvent(new Event('input', {bubbles: true}));
                    }
                });
                menuObserver.observe(document.body, {childList: true, subtree: true});
                widget = new google.maps.places.Autocomplete(input, {
                    types: ['geocode', 'establishment'], componentRestrictions: {country: 'fi'},
                    fields: ['address_components', 'formatted_address', 'geometry', 'place_id', 'name'],
                });
                listener = widget.addListener('place_changed', () => {
                    const place = widget!.getPlace();
                    const currentSelection = selectionRevision === revision;
                    selectionRevision = undefined;
                    if (!currentSelection || !hasPlaceCoordinates(place)) {
                        input.value = latest.current.value;
                        if (currentSelection) setNeedsSelection(true);
                        return;
                    }
                    setNeedsSelection(false);
                    lastSelectedRevision = revision;
                    latest.current.onSelect(place, input.value);
                });
                setStatus('ready');
                setShowLoading(false);
            }).catch(() => {
                menuObserver?.disconnect();
                if (!disposed) setStatus('failed');
            })
                .finally(() => {window.clearTimeout(loadingTimer); loading = false;});
        };
        return () => {
            disposed = true;
            menuObserver?.disconnect();
            listener?.remove();
            input.removeEventListener('input', edit);
            input.removeEventListener('keydown', keyDown, true);
            document.removeEventListener('mousedown', pointerDown, true);
            document.removeEventListener('touchstart', pointerDown, true);
        };
    }, [inputRef]);

    return <>
        <input ref={inputRef} id="google-search-location" name={name} value={value}
               autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
               className="w-100 searchbox-input" placeholder={translateService.translate('vehicleSearch.search')}
               onChange={() => {}} onFocus={() => start.current()}
               onKeyDown={event => {if (event.key === 'Enter') event.preventDefault();}}
               aria-busy={status === 'loading'} aria-describedby="location-search-status"/>
        <div id="location-search-status" role="status" style={{fontSize: '0.875rem', position: 'absolute', left: 0, right: 0}}>
            {status === 'loading' && showLoading ? translateService.translate('googleLoading.addressLoading') :
                status === 'failed' ? <>{translateService.translate('googleLoading.addressFailed')} <button type="button" onClick={() => start.current()}>{translateService.translate('googleLoading.retry')}</button></> :
                    needsSelection ? translateService.translate('googleLoading.chooseAddress') : null}
        </div>
    </>;
}
